import axios from "@/axios";
import { EventsTickets } from "@/utils/api";
import { validateParamsHaveValues } from "../../../utils/utils";

/**
 * @typedef {object} EtsAuthToken
 * @property {string} accessToken The JWT
 * @property {string} tokenType "Bearer"
 * @property {number} expiresIn The number of seconds until the JWT expires
 */

export default {
  /**
   * Gets the `storeId` and the Client's access `token` needed to make Store API requests.
   * This will get or create a new Store, and then get the auth token or login for the client.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @returns {Promise<object>}
   */
  async getStoreRequestParams({ dispatch }, { charityId, clientJwt }) {
    validateParamsHaveValues({ charityId });

    const store = await dispatch("getOrCreateStore", { charityId, clientJwt });

    const tokenData = await dispatch("getClientTokenOrLogin", {
      charityId,
      storeId: store.id,
      clientJwt,
    });

    return {
      storeId: store.id,
      token: tokenData.accessToken,
      authHeader: [tokenData.tokenType, tokenData.accessToken].join(" "),
    };
  },
  /**
   * Gets the base headers required to make an authenticated ETS request.
   *
   * Note: this WILL perform the actions done by `getStoreRequestParams`,
   * which includes possibly creating a new store, and logging in.
   *
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @returns {Promise<object>}
   */
  async getStoreRequestHeaders({ dispatch }, { charityId, clientJwt }) {
    const params = await dispatch("getStoreRequestParams", {
      charityId,
      clientJwt,
    });
    return {
      "X-Harness-Store-ID": params.storeId,
      Authorization: params.authHeader,
    };
  },
  /**
   * Get the existing client token if it is still valid, otherwise try to login again.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @param {number} params.storeId The ID of the charity's ETS Store
   * @returns {Promise<EtsAuthToken>}
   */
  async getClientTokenOrLogin(
    { getters, dispatch, commit },
    { charityId, storeId, clientJwt }
  ) {
    validateParamsHaveValues({ charityId, clientJwt });

    // If the token is still valid, return that immediately
    if (getters.isClientTokenValid(charityId)) {
      return getters.getClientToken(charityId);
    }

    // Remove the no longer valid token (if it exists)
    commit("REMOVE_CLIENT_TOKEN", charityId);

    return await dispatch("loginClient", {
      charityId,
      storeId: storeId || getters.getStoreByCharityId(charityId)?.id,
      clientJwt,
    });
  },
  /**
   * Login to ETS as an admin.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @param {number} params.storeId The ID of the charity's ETS Store
   * @returns {Promise<EtsAuthToken>}
   */
  async loginClient({ commit }, { charityId, storeId, clientJwt }) {
    validateParamsHaveValues({ charityId, storeId, clientJwt });

    let params = new URLSearchParams();
    params.append("jwt", clientJwt);

    try {
      const tokenData = await axios.post(
        `/event-service/sign-in/admin/${charityId}`,
        params,
        {
          "X-Harness-Store-ID": storeId,
        }
      );

      if (!tokenData?.data?.accessToken) {
        throw new Error(
          "Unable to login to Event system, response is missing expected fields!"
        );
      }

      commit("SET_CLIENT_TOKEN", { charityId, tokenData: tokenData.data });
      return tokenData.data;
    } catch (error) {
      throw new Error(
        "Failed to login to the event system. Please try again later.",
        { cause: error }
      );
    }
  },
  /**
   * Get data about a charity's Store by:
   *   1. Checking if that data is already saved in this Vuex store.
   *   2. Performing a lookup to see if a Store already exists for the Charity.
   *   3. If no Store exists, creating a new Store for the Charity.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @returns {Promise<object>}
   */
  async getOrCreateStore({ getters, dispatch }, { charityId, clientJwt }) {
    // Check to see if we've already cached data about the store
    let existingStore = getters.getStoreByCharityId(charityId);
    if (existingStore) {
      return existingStore;
    }

    try {
      // Try to find an existing Store for the charity
      return await dispatch("fetchExistingStoreByCharityId", { charityId });
    } catch (error) {
      // If the error is not a 404, then something else has gone wrong; rethrow for handling elsewhere.
      if (error?.response?.status !== 404) {
        throw error;
      }

      // Otherwise, create a new store
      return await dispatch("createStore", { charityId, clientJwt });
    }
  },
  /**
   * Fetch data about the store belonging to the given `charityId`, as long as one exists.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @returns {Promise<object|null>} The store object, or NULL if no store exists for the charity.
   */
  async fetchExistingStoreByCharityId({ commit }, { charityId }) {
    validateParamsHaveValues({ charityId });

    const response = await axios.get(
      `/event-service/store/charity/${charityId}`
    );

    if (!response?.data?.data?.charityId === charityId) {
      return null;
    }

    commit("SET_STORE", { charityId, store: response?.data?.data });

    return response?.data?.data;
  },
  /**
   * Create a new Store for the given Charity.
   * @param {object} context Vuex context object
   * @param {object} params Parameters for the action
   * @param {string} params.charityId The ID of the charity
   * @param {string} params.clientJwt The Client's HCA auth JWT
   * @returns {Promise<object>}
   */
  async createStore({ commit }, { charityId, clientJwt }) {
    validateParamsHaveValues({ charityId, clientJwt });

    const response = await axios.post("/event-service/store", {
      charityId,
      jwt: clientJwt,
    });

    if (!response?.data?.data?.store?.id) {
      return null;
    }

    commit("SET_STORE", {
      charityId,
      store: response?.data?.data?.store,
    });

    return response?.data?.data?.store;
  },
  async fetchEvents({ commit, state }, { charityId, force = false }) {
    if (state.eventsLoading) {
      return state.events;
    }

    const isCacheValid =
      parseInt(new Date().getTime() / 1000) - state.lastUpdate < 60 * 14; //14 min cache
    if (!state.eventsLoading && force !== true && isCacheValid) {
      return state.events;
    }

    commit("LOADING_EVENTS");

    // Hack: Ensure store exists and client is logged in first, before trying to load events and tickets.
    // This avoids the two methods BOTH trying to create a store and/or log in at the same time.
    try {
      await EventsTickets.getStoreRequestHeaders(charityId);
    } catch (error) {
      commit("SET_EVENTS", []);
      commit("CLEAR_TICKETS");
      return [];
    }

    // Begin loading both the Events and Transactions in parallel
    const fetchEvents = EventsTickets.getEventsList(charityId);
    const fetchTransactions = EventsTickets.getAllTransactionsList(charityId);

    try {
      const eventsResponse = await fetchEvents;
      commit("SET_EVENTS", eventsResponse.events);
    } catch (error) {
      // Set to empty to ensure the loading state is turned off.
      commit("SET_EVENTS", []);
    }

    try {
      const transactionsResponse = await fetchTransactions;
      commit("CLEAR_TICKETS");
      commit("SET_TICKETS", transactionsResponse.transactions);
    } catch (error) {
      commit("CLEAR_TICKETS");
    }

    return state.events;
  },
};
